<template>
	<header>
		<nav
			class="main fix-on-scroll"
			:class="[page && page.headingImage ? '' : 'no-slider', { 'menu-open': showMenu }]"
		>
			<div class="row">
				<div class="columns column12 flex-row">
					<div class="flex-row">
						<div class="menu-button button" @click="toggleMenu">
							<div class="hamburger">
								<span :class="{ 'top-line': true, active: showMenu }"></span>
								<span :class="{ 'middle-line': true, active: showMenu }"></span>
								<span :class="{ 'bottom-line': true, active: showMenu }"></span>
							</div>

							<div v-if="showMenu">
								<span class="label">{{ $t('close') }}</span>
							</div>
							<div v-else>
								<span class="label">{{ $t('menu') }}</span>
							</div>
						</div>
						<div v-if="!showMenu" class="hotelselect-button">
							<hotel-select :items="hotels" />
						</div>
					</div>

					<button class="logo" aria-label="Back to homepage" @click="handleLogoClick" />
					<div class="nav-buttons nav-buttons-right">
						<lang-select :items="page.langmenu" />
						<div v-if="!showMenu" class="divider"></div>
						<a v-if="!showMenu" :href="`mailto:${defaults[locale].website.email}`">
							<font-awesome-icon icon="fa-sharp fa-regular fa-envelope" size="1x" />
						</a>
						<a v-if="!showMenu" :href="`tel:${$phoneRaw(defaults[locale].website.phone)}`">
							<font-awesome-icon icon="fa-sharp fa-regular fa-phone" size="1x" />
						</a>
						<book-button class="button button-cta reserve-now right" aria-label="Reserve now" href="#">
							<font-awesome-icon class="icon" icon="fa-light fa-calendar-days" size="1x" />
							<span>{{ defaults[locale].website.book }}</span>
						</book-button>
					</div>
				</div>
			</div>
			<div
				class="nav-container fix-on-scroll"
				:class="[page && page.headingImage ? '' : 'no-slider', { active: showMenu }]"
			>
				<div class="row">
					<ul class="main-nav">
						<li
							v-for="(item, index) in topmenu[locale]"
							:key="item.filename"
							:class="[showMenu ? 'fade-in-up' : 'opacity-0', `delay-${index}`]"
						>
							<nuxt-link :exact="item.slug === 'index'" :to="`${item.filename}`" @click="hideMenu">
								{{ item.header }}
							</nuxt-link>
							<submenu
								v-if="item.submenu.length"
								:hide-all="!showMenu"
								:item="item"
								:subitems="item.submenu"
								@hide-menu="hideMenu"
							/>
						</li>
						<li v-if="page" class="lang-nav">
							<div
								v-for="(item, index) in page.langmenu"
								:key="`${item.filename}-${item.language}`"
								:class="[showMenu ? 'fade-in-up' : '', `delay-${index + topmenu[locale].length}`]"
								class="lang-item"
							>
								<nuxt-link
									class="flag"
									:class="item.language"
									:to="`${item.filename}`"
									@click="hideMenu"
								>
									<span>{{ item.language }}</span>
								</nuxt-link>
							</div>
						</li>
					</ul>
					<div class="nav-contact" :class="{ 'fade-in': showMenu, 'delay-9': showMenu }">
						<a :href="`tel:${$phoneRaw(defaults[locale].website.phone)}`">
							<font-awesome-icon icon="fa-sharp fa-regular fa-phone" size="1x" />
							<span>{{ defaults[locale].website.phone }}</span>
						</a>

						<a :href="`mailto:${defaults[locale].website.email}`">
							<font-awesome-icon icon="fa-sharp fa-regular fa-envelope" size="1x" />
							<span>{{ defaults[locale].website.email }}</span>
						</a>
					</div>
					<social-media
						class="nav-contact"
						:class="{ 'fade-in': showMenu, 'delay-9': showMenu }"
						:socials="socials"
					/>
					<div />
				</div>
			</div>
		</nav>
	</header>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { defaults, socials, fetchDefaults } = useDefaults();
const { locale } = useI18n();
const localeRoute = useLocaleRoute();
const route = useRoute();

await fetchPage();
await fetchDefaults();

const { data: topmenu } = await useWebsiteFetch('topmenu');

const { data: hotelsraw } = await useWebsiteFetch('hotels', {
	api: 'group',
	query: { language: locale.value },
	key: `${locale.value}/hotels`,
});

const hotels = Array.isArray(hotelsraw.value)
	? hotelsraw.value.filter((hotel) => hotel.showInHotelselect === true)
	: [];
const showMenu = ref(false);

const toggleMenu = () => {
	showMenu.value = !showMenu.value;
	document.body.classList.toggle('locked', showMenu.value);
};

const hideMenu = () => {
	showMenu.value = false;
	document.body.classList.remove('locked');
};

const handleLogoClick = () => {
	if (showMenu.value) {
		hideMenu();
	}

	if (route.name?.startsWith('index')) {
		window.scrollTo(0, 0);
	} else {
		const route = localeRoute('index');
		navigateTo(route.fullPath);
	}
};
</script>

<style lang="scss" scoped>
@keyframes fade-in-up {
	0% {
		opacity: 0;
		transform: translateY(20px);
	}

	100% {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes fade-in {
	0% {
		opacity: 0;
	}

	100% {
		opacity: 1;
	}
}

.fade-in-up {
	opacity: 0;
	animation: fade-in-up 0.5s ease-out forwards;
}

.fade-in {
	opacity: 0;
	animation: fade-in 1s ease-out forwards;
}

/* stylelint-disable-next-line at-rule-no-unknown */
@for $i from 0 through 20 {
	.delay-#{$i} {
		animation-delay: #{0.5 + (0.05 * $i)}s;
	}
}

.opacity-0 {
	opacity: 0;
}

.hamburger {
	width: 35px;
	height: 35px;
	margin-right: 15px;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	cursor: pointer;
	position: relative;
}

.hamburger span {
	width: 35px;
	height: 2px;
	background-color: #fff;
	position: absolute;
	transition: all 0.2s ease-in-out;
}

.top-line {
	transform: translateY(-10px);
}

.middle-line {
	opacity: 1;
	align-self: flex-start;
	width: 25px !important;
}

.bottom-line {
	transform: translateY(10px);
}

.active.top-line {
	transform: rotate(45deg);
}

.active.middle-line {
	opacity: 0;
}

.active.bottom-line {
	transform: rotate(-45deg);
}

.logo {
	all: unset;
	cursor: pointer;
	background: url('~/assets/images/saintsauveur.svg') no-repeat center center;
	background-size: 270px;
	width: 270px;
	height: 70px;
	position: absolute;
	left: 0;
	right: 0;
	margin: 0 auto;
	animation: fade-from-top 1s ease;
	transition: all 0.3s ease-in-out;
}

nav.main {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: 100;
	padding: 0;
	transition: all 0.3s ease-in-out;
	overflow: visible;
	height: 105px;
	border-bottom: 0.5px solid #fff;
}

nav .row {
	overflow: visible;
	z-index: 102;
	position: relative;
	display: flex;
	justify-content: center;
	align-items: center;
	flex-flow: column;
	height: 100%;
}

nav.main.scroll,
nav.main.no-slider {
	background: #041e42;
	height: 70px;
	border-bottom: none;

	.nav-buttons-right {
		height: 100%;
		top: 0;
	}

	.logo {
		background-size: 190px;
		width: 190px;
		height: 50px;
	}
}

nav.main.scroll:not(.menu-open),
nav.main.no-slider:not(.menu-open) {
	.nav-container {
		backdrop-filter: none;
		background: none;
	}
}

.reserve-now {
	display: flex;
	flex-flow: row nowrap;
	justify-content: center;
	align-items: center;
	padding: 10px 25px 10px 20px;

	svg {
		margin: 0 12px 2px 0;
	}
}

.menu-button {
	background: none;
	padding: 0;
	margin: 0;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	text-align: left;
	display: flex;
	flex-flow: row nowrap;
	justify-content: space-between;
	align-items: center;
	gap: 10px;

	&:hover {
		opacity: 1;
		background: none;
	}
}

nav .flex-row,
.nav-buttons {
	justify-content: space-between;
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	height: 100%;
	transition: all 0.3s ease-in-out;
	max-height: 104px;
}

.hotelselect-button,
.nav-buttons {
	gap: 22px;
	color: #fff;
	animation: fade-from-right 0.7s ease;

	.hotel-select,
	.lang-select {
		font-size: 14px;
		font-weight: 500;
		letter-spacing: 1.2px;
	}

	.divider {
		border-right: 1px solid var(--alternating-background-color);
		height: 70%;
	}

	a {
		color: #fff;
		text-decoration: none;
		margin: 0;
		opacity: 0.85;

		&.button {
			opacity: 1;
			height: 100%;
			padding: 0 50px;
		}

		&:hover {
			opacity: 1;
		}
	}
}

.nav-buttons-right {
	.icon {
		display: none;
		font-size: 1.125em;
	}
}

.nav-container.scroll {
	transform: translateY(calc(-100vh + 70px));
	opacity: 0;
}

.nav-container.no-slider {
	height: 0;
}

.nav-container {
	height: 100%;
	background: rgb(119 116 110 / 20%);
	backdrop-filter: blur(16px);
	padding: 30px 50px;
	position: fixed;
	top: 0;
	left: 0;
	padding-top: 80px;
	z-index: 101;
	width: 100%;
	opacity: 1;
	transition: transform 0.4s ease-in-out 0.1s;
	transform: translateY(calc(-100vh + 104px));

	.row {
		display: flex;
		flex-flow: column nowrap;
		justify-content: space-between;
		align-items: center;
		height: 100%;
	}
}

.nav-container.active {
	transform: matrix(1, 0, 0, 1, 0, 0);
	opacity: 1;
	height: 100%;
}

.lang-nav {
	display: none;
	list-style: none;
	text-align: center;
	padding-top: 30px;
	justify-content: center;
	margin: auto;
	flex-flow: row;

	.lang-item {
		width: 30px;
		position: relative;
	}

	a {
		color: #fff;
		display: inline-block;
		flex-flow: row nowrap;
		align-items: center;
		justify-content: center;
		font-family: var(--body-font-family) !important;
		font-size: 14px !important;
		font-weight: 500;
		text-transform: uppercase;
		position: relative;
		line-height: 1px !important;
		padding: 0 !important;

		&::before,
		&::after {
			content: none !important;
			display: none !important;
		}
	}

	img {
		max-width: 16px;
		float: left;
		margin: 0 5px 0 0;
	}
}

.lang-nav a::before {
	transform: translateY(-50%) translateX(35%) scale(0.5) !important;
}

.lang-nav a::after {
	transform: translateY(-50%) translateX(-35%) scale(0.5) !important;
}

.mobile-lang-nav {
	margin: 40px 0 0;
	font-size: 14px;
	display: none;
}

.mobile-lang-nav img {
	max-width: 16px;
	margin: 0 10px 0 0;
}

.main-nav .mobile-lang-nav a {
	width: 70%;
	display: none;
	font-size: 14px;
	line-height: 28px;
}

/* Fullscreen Nav */
.main-nav {
	list-style: none;
	min-width: 25%;
	text-align: center;
	padding-top: 100px;

	.nav-item {
		opacity: 0;
		transform: translateY(20px);
		transition:
			opacity 0.3s ease-out,
			transform 0.3s ease-out;
	}

	a {
		color: #fff;
		font-size: 24px;
		font-family: var(--heading-font-family);
		position: relative;
		padding-left: 35px;
		padding-right: 35px;
		line-height: 64px;

		&::before,
		&::after {
			content: '';
			display: block;
			aspect-ratio: 1;
			width: 25px;
			height: 25px;
			background-image: url('~/assets/images/starVectorWhite.svg');
			background-size: cover;
			background-repeat: no-repeat;
			background-position: center;
			position: absolute;
			top: 50%;
			transform: translateY(-50%);
			opacity: 0;
			transition: all 0.3s ease;
			pointer-events: none;
		}

		&::before {
			left: -35px;
		}

		&::after {
			right: -35px;
		}

		&:hover::before,
		&:hover::after,
		&.router-link-active::before,
		&.router-link-active::after {
			opacity: 1;
		}

		&:hover::before,
		&.router-link-active::before {
			left: 0;
		}

		&:hover::after,
		&.router-link-active::after {
			right: 0;
		}
	}
}

.nav-contact {
	align-self: flex-end;
	width: 100%;
	display: flex;
	flex-flow: row wrap;
	align-items: center;
	justify-content: center;
	gap: 20px;
	padding: 20px 0 0;
	opacity: 0;

	svg {
		margin-right: 5px;
	}

	.socials {
		margin: 0;
	}
}

.nav-contact > a {
	color: #fff;
	display: block;
	font-weight: 300;
	margin: 0;
}

.nav-contact > a i {
	margin: 0 10px 0 0;
}

.close-wrapper {
	align-self: flex-start;
}

.nav-close {
	color: #fff;
	cursor: pointer;
	font-size: 54px;
	padding: 0 0 0 12px;
}

/* Breadcrumbs */
.breadcrumbs ul {
	list-style: none;
	display: flex;
	flex-wrap: wrap;
	justify-content: flex-start;
	align-items: center;
	margin: 0 0 75px;
}

.breadcrumbs li {
	margin: 0 10px 0 0;
}

.breadcrumbs i {
	font-size: 11px;
}

.breadcrumbs a {
	color: var(--body-color);
}

.breadcrumbs a.active {
	color: var(--cta-color);
}

@media (max-width: 1640px) {
	.nav-buttons-right {
		right: 0;
		top: 0;
		height: 104px;
		margin-right: 0 !important;
	}

	nav.main.scroll,
	nav.main.no-slider {
		.nav-buttons-right {
			height: 70px;
			top: 0;
		}
	}
}

@media (max-width: 1641px) {
	.nav-buttons-right {
		position: fixed;
	}
}

@media (max-height: 740px) {
	.nav-container {
		overflow-y: scroll;
	}

	.lang-nav {
		position: relative;
		bottom: auto;
		left: auto;
		z-index: 1;
	}

	.main-nav {
		z-index: 2;
		position: relative;
	}
}

@media (max-width: 920px) {
	.reserve-now {
		padding: 0;

		svg {
			margin: 0;
		}
	}

	.nav-buttons .lang-select {
		display: none;
	}

	.nav-buttons-right {
		.icon {
			display: block;
		}
	}

	.menu-button {
		justify-content: center;
	}

	.lang-nav {
		display: flex;
	}

	.close-wrapper {
		align-self: flex-end;
	}

	.main-nav {
		a {
			line-height: 50px;
		}
	}
}

@media (max-width: 600px) {
	.hamburger {
		width: 20px;
		height: 20px;
	}

	.hamburger span {
		width: 20px;
		height: 2px;
	}

	.middle-line {
		width: 27px;
	}

	.nav-container {
		padding: 10px 25px 25px;
		transform: translateY(calc(-100vh + 69px));
	}

	.main-nav,
	.contact-info,
	.lang-nav {
		width: 100%;
	}

	nav.main {
		.flex-row {
			padding: 0;
		}

		height: 70px;
	}

	.nav-buttons-right {
		width: 70px;
		height: 69px;
	}

	nav.main.scroll,
	nav.main.no-slider {
		padding: 18px 2px;

		.logo {
			background-size: 180px;
			width: 180px;
			height: 70px;
		}
	}

	.logo {
		background-size: 180px;
		width: 180px;
		height: 70px;
	}

	.contact-info {
		margin: 25px auto 0;
	}

	.lang-nav {
		margin-top: 0;
	}

	nav .buttons .button {
		svg {
			margin-right: 0;
			font-size: 14px;
		}

		span {
			display: none;
		}
	}

	.buttons a.button {
		padding: 0 !important;
	}

	.reserve-now {
		padding: 0 !important;
		width: 100%;
		height: 70px;
	}

	.menu-button {
		gap: 0;
	}
}

@media (max-width: 480px) {
	nav.main.scroll,
	nav.main.no-slider {
		.logo {
			background-size: 130px;
			width: 130px;
			height: 50px;
		}
	}

	.main-nav a {
		font-size: 20px;
	}

	.logo {
		background-size: 130px;
		width: 130px;
		height: 50px;
	}

	.nav-container {
		padding: 10px 25px 25px;
		transform: translateY(calc(-100svh + 69px));

		.main-nav {
			margin-bottom: 0;
		}

		.lang-nav {
			padding-top: 10px;
		}

		.nav-contact {
			padding: 0;
		}
	}
}
</style>
